(() => {
  const STYLE_ID = 'perfumiarz-shopify-forms-fonts';
  const PRIVACY_LINK_ATTRIBUTE = 'data-perfumiarz-privacy-link';
  const PRIVACY_LABELS = ['Privacy Policy', 'Polityka prywatności', 'Politykę prywatności'];
  const SUBJECT_ALIASES = [
    ['general question', 'pytanie ogólne'],
    ['order support', 'obsługa zamówienia', 'wsparcie w zamówieniach'],
    ['product question', 'pytanie o produkt'],
    ['collaboration', 'współpraca'],
  ];
  const TOPIC_OPTION_SELECTOR = 'div[class*="_optionListItem_"] > div[role="option"]';
  const bilingualTopicControls = new WeakSet();

  // These are registered Shopify Forms choices, not new client-side POST values.
  function identifyTopicOption(element) {
    const id = normalizeText(element.id);
    for (let index = 0; index < SUBJECT_ALIASES.length; index += 1) {
      const aliases = SUBJECT_ALIASES[index];
      for (let aliasIndex = 0; aliasIndex < aliases.length; aliasIndex += 1) {
        // Shopify's option ID contains the original choice value. The visible
        // label may already have been translated by the app or our legacy UI.
        if (id.endsWith(`-${aliases[aliasIndex]}`)) {
          return { index, language: aliasIndex === 0 ? 'en' : 'pl' };
        }
      }
    }
    return null;
  }

  function getBilingualTopicOptions(elements, language) {
    if (!['pl', 'en'].includes(language) || elements.length !== 8) return null;
    const identified = elements.map((element) => ({ element, topic: identifyTopicOption(element) }));
    if (identified.some(({ topic }) => !topic)) return null;
    const keys = new Set(identified.map(({ topic }) => `${topic.language}:${topic.index}`));
    if (keys.size !== 8) return null;
    return identified;
  }

  function bindBilingualTopicControls(container) {
    if (bilingualTopicControls.has(container)) return;
    bilingualTopicControls.add(container);
    const visibleOptions = () => Array.from(container.querySelectorAll(TOPIC_OPTION_SELECTOR))
      .filter((option) => !option.parentElement.hidden);
    const enabled = () => container.dataset.perfumiarzBilingualTopics === 'true';

    container.addEventListener('click', (event) => {
      const option = event.target.closest?.('[role="option"]');
      if (enabled() && option?.parentElement.hidden) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);

    // Shopify initially focuses its first registered option, which may be hidden
    // in PL. Redirect focus without changing the app's selected value.
    container.addEventListener('focusin', (event) => {
      if (!enabled()) return;
      const option = event.target.closest?.('[role="option"]');
      if (option?.parentElement.hidden) visibleOptions()[0]?.focus();
    }, true);

    container.addEventListener('keydown', (event) => {
      if (!enabled() || event.ctrlKey || event.altKey || event.metaKey) return;
      const option = event.target.closest?.('[role="option"]');
      const toggle = event.target.closest?.('button[class*="_selectToggle_"]');
      const visible = visibleOptions();
      if ((!option && !toggle) || !visible.length) return;

      let target;
      const index = visible.indexOf(option);
      if (event.key === 'ArrowDown') target = visible[(index + 1) % visible.length];
      else if (event.key === 'ArrowUp') target = visible[(index < 0 ? visible.length - 1 : index - 1 + visible.length) % visible.length];
      else if (event.key === 'Home') target = visible[0];
      else if (event.key === 'End') target = visible[visible.length - 1];
      else if (event.key === 'Enter' || event.key === ' ') {
        // Use Shopify's own click handler so its form state receives the actual
        // registered value. Never synthesize a translated submission payload.
        event.preventDefault();
        event.stopImmediatePropagation();
        (index >= 0 ? option : visible[0]).click();
        container.querySelector('button[class*="_selectToggle_"]')?.focus();
        return;
      } else if (event.key.length === 1) {
        const rotated = [...visible.slice(index + 1), ...visible.slice(0, index + 1)];
        target = rotated.find((item) => normalizeText(item.textContent).startsWith(normalizeText(event.key)));
        // Suppress Shopify's typeahead across all eight (including hidden) values.
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      if (target) {
        event.preventDefault();
        event.stopImmediatePropagation();
        target.focus();
      }
    }, true);
  }

  function filterBilingualTopics(form, options) {
    if (!options.bilingualTopics) return false;
    const container = form.querySelector('div[class*="_selectContainer_"]');
    if (!container) return false;
    const elements = Array.from(container.querySelectorAll(TOPIC_OPTION_SELECTOR));
    // A closed dropdown is unmounted by Shopify. Keep the previously detected mode.
    if (!elements.length) return container.dataset.perfumiarzBilingualTopics === 'true';
    const identified = getBilingualTopicOptions(elements, options.language);
    if (!identified) {
      delete container.dataset.perfumiarzBilingualTopics;
      elements.forEach((element) => { element.parentElement.hidden = false; });
      return false;
    }
    container.dataset.perfumiarzBilingualTopics = 'true';
    identified.forEach(({ element, topic }) => {
      element.parentElement.hidden = topic.language !== options.language;
    });
    bindBilingualTopicControls(container);
    const active = form.getRootNode().activeElement;
    if (elements.includes(active) && active.parentElement.hidden) {
      identified.find(({ topic }) => topic.language === options.language)?.element.focus();
    }
    return true;
  }

  const styles = `
    [data-perfumiarz-bilingual-topics="true"] div[class*="_optionListItem_"][hidden] {
      display: none !important;
    }
    :host {
      display: block !important;
      width: 100% !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      --inline-container-max-width: 100%;
      --inline-container-min-height: 0px;
    }

    section[class*="_inline_"] {
      display: block !important;
      width: 100% !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
    }

    section[data-sizing="form-wrapper"] {
      display: block !important;
      width: 100% !important;
      max-width: none !important;
      min-width: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
      overflow: visible !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      background: transparent !important;
    }

    section[data-sizing="form-wrapper"] > div[class*="_gridItem_"]:first-child {
      display: none !important;
    }

    div[class*="_gridItemContent_"] {
      width: 100% !important;
      padding: 0 !important;
      background: transparent !important;
    }

    section[class*="_formHeader_"] {
      margin: 0 0 3.6rem !important;
      text-align: center !important;
    }

    section[class*="_formHeader_"] h2 {
      margin: 0 !important;
      font-family: "ABC Gaisyr", Georgia, serif !important;
      font-size: 2.2rem !important;
      font-weight: 400 !important;
      line-height: 3.2rem !important;
      letter-spacing: -0.02em !important;
      color: #100f0f !important;
      text-align: center !important;
    }

    section[class*="_formHeader_"] > div {
      display: none !important;
    }

    form[data-testid="form"] {
      display: flex !important;
      flex-direction: column !important;
      gap: 0 !important;
      width: 100% !important;
      padding: 0 !important;
      overflow: visible !important;
    }

    form,
    fieldset,
    legend,
    label,
    input,
    textarea,
    select,
    option,
    button,
    p,
    span,
    a {
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-weight: 400;
    }

    form[data-testid="form"] > div[class*="_selectContainer_"],
    form[data-testid="form"] > div[class*="_formFieldContainer_"] {
      width: 100% !important;
      margin: 0 0 1.2rem !important;
    }

    form[data-testid="form"] > div[class*="_formFieldContainer_"]:has(textarea),
    form[data-testid="form"] > *:has(textarea) {
      order: 40 !important;
      margin-bottom: 3.6rem !important;
    }

    p[class*="_label_"] {
      display: none !important;
    }

    button[class*="_selectToggle_"] {
      box-sizing: border-box !important;
      width: 100% !important;
      min-height: 3.6rem !important;
      max-height: none !important;
      padding: 0.8rem 1.6rem !important;
      border: 0.05rem solid #c9c9c9 !important;
      border-radius: 0.2rem !important;
      background: #f8f9f8 !important;
      box-shadow: inset 0 0 1.05rem rgba(115, 115, 115, 0.1) !important;
      color: #313131 !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-size: 1.4rem !important;
      font-weight: 400 !important;
      line-height: 2rem !important;
      letter-spacing: 0.04em !important;
    }

    span[class*="_selectToggleText_"] {
      color: #666 !important;
    }

    span[class*="_selectToggleText_"]:empty::before {
      content: attr(data-perfumiarz-topic-placeholder);
      color: #666;
    }

    span[class*="_arrowDownIcon_"] {
      position: relative !important;
      display: block !important;
      flex: 0 0 0.9rem !important;
      width: 0.9rem !important;
      height: 1.4rem !important;
      margin: 0 !important;
      color: #9a9a9a !important;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='9' height='14' viewBox='0 0 9 14' fill='none'%3E%3Cpath d='M8.14844 9.3125C7.71657 9.3125 7.07168 9.73913 6.53039 10.171C5.83195 10.7263 5.22257 11.3898 4.75752 12.1505C4.40889 12.7209 4.07422 13.4123 4.07422 13.9687M4.07422 13.9687C4.07422 13.4123 3.73955 12.7203 3.39091 12.1505C2.92529 11.3898 2.3159 10.7263 1.61863 10.171C1.07676 9.73913 0.430704 9.3125 0 9.3125M4.07422 13.9687L4.07422 0' stroke='%239a9a9a' stroke-width='0.582031'/%3E%3C/svg%3E") !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      background-size: 0.9rem 1.4rem !important;
      transition: transform 0.2s ease !important;
    }

    span[class*="_arrowDownIcon_"] svg {
      display: none !important;
    }

    span[class*="_arrowDownIcon_"]::before {
      content: none !important;
    }

    span[class*="_arrowDownIcon_"]::after {
      content: none !important;
    }

    button[class*="_selectToggle_"][aria-expanded="true"] span[class*="_arrowDownIcon_"] {
      transform: rotate(180deg) !important;
    }

    div[class*="_dropdownContainer_"] {
      z-index: 30 !important;
      max-height: none !important;
      margin-top: -0.05rem !important;
      border: 0.05rem solid #c9c9c9 !important;
      border-top: 0 !important;
      border-radius: 0 0 0.2rem 0.2rem !important;
      overflow: visible !important;
      background: #f8f9f8 !important;
      box-shadow: inset 0 0 1.05rem rgba(115, 115, 115, 0.1) !important;
    }

    div[class*="_optionList_"] {
      max-height: none !important;
      padding: 1.2rem 1.6rem !important;
      overflow: visible !important;
      background: #f8f9f8 !important;
    }

    div[class*="_optionListItem_"] {
      margin: 0 !important;
      border-radius: 0 !important;
      border-bottom: 0.05rem solid #dcdcdc !important;
      background: transparent !important;
      box-shadow: none !important;
      outline: 0 !important;
    }

    div[class*="_optionListItem_"]:last-child {
      border-bottom: 0 !important;
    }

    div[class*="_optionListItem_"] > div {
      padding: 0 0 1.2rem !important;
      border-radius: 0 !important;
      background: transparent !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-size: 1.4rem !important;
      line-height: 2rem !important;
      letter-spacing: 0.04em !important;
      color: #666 !important;
      outline: 0 !important;
    }

    div[class*="_optionListItem_"] + div[class*="_optionListItem_"] > div {
      padding-top: 1.2rem !important;
    }

    div[class*="_optionListItem_"] > div:focus,
    div[class*="_optionListItem_"] > div:focus-visible,
    div[class*="_optionListItem_"]:hover,
    div[class*="_optionListItemSelected_"] {
      background: transparent !important;
      box-shadow: none !important;
      outline: 0 !important;
    }

    div[class*="_optionListItem_"] > div:focus,
    div[class*="_optionListItem_"] > div:focus-visible,
    div[class*="_optionListItem_"]:hover > div,
    div[class*="_optionListItemSelected_"] > div {
      color: #313131 !important;
      background: transparent !important;
      outline: 0 !important;
    }

    input[class*="_formInputField_"],
    textarea[class*="_textArea_"] {
      box-sizing: border-box !important;
      width: 100% !important;
      min-height: 3.6rem !important;
      padding: 0.8rem 1.6rem !important;
      border: 0.05rem solid #c9c9c9 !important;
      border-radius: 0.2rem !important;
      outline: 0 !important;
      background: #f8f9f8 !important;
      box-shadow: inset 0 0 1.05rem rgba(115, 115, 115, 0.1) !important;
      color: #313131 !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-size: 1.4rem !important;
      font-weight: 400 !important;
      line-height: 2rem !important;
      letter-spacing: 0 !important;
    }

    textarea[class*="_textArea_"] {
      min-height: 8rem !important;
      padding-top: 1rem !important;
      resize: vertical !important;
    }

    output[role="alert"] {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    input[class*="_formInputField_"][aria-invalid="true"],
    textarea[class*="_textArea_"][aria-invalid="true"],
    textarea[class*="_textArea_"][class*="_invalid_"],
    button[class*="_selectToggle_"][class*="_selectorError_"] {
      border-color: #edd7d3 !important;
      background: #edd7d3 !important;
      box-shadow: inset 0 0 1.05rem rgba(115, 115, 115, 0.08) !important;
    }

    div[class*="_formFieldContainer_"]:has(textarea)::before {
      display: none !important;
    }

    label[class*="_formInputFieldLabel_"] {
      top: 50% !important;
      left: 1.6rem !important;
      transform: translateY(-50%) !important;
      color: #9a9a9a !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-size: 1.4rem !important;
      font-weight: 400 !important;
      line-height: 2rem !important;
      opacity: 1;
    }

    div[class*="_formFieldContainer_"]:has(textarea) label[class*="_formInputFieldLabel_"] {
      top: 1rem !important;
      transform: none !important;
    }

    div[class*="_formFieldContainer_"]:focus-within label[class*="_formInputFieldLabel_"],
    label[class*="_formInputFieldLabel_"][class*="_inputFilled_"] {
      opacity: 0 !important;
    }

    form[data-testid="form"] > div[class*="_consentCheckboxContainer_"],
    form[data-testid="form"] > fieldset[class*="_checkboxFieldSet_"] {
      order: 50 !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      align-self: stretch !important;
      margin: 0 !important;
      padding: 0 !important;
      border: 0 !important;
    }

    form[data-testid="form"] > *:has(input[type="checkbox"]),
    form[data-testid="form"] > [data-perfumiarz-consent-order] {
      box-sizing: border-box !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      flex: 0 0 100% !important;
      align-self: stretch !important;
    }

    form[data-testid="form"] > [data-perfumiarz-consent-order="1"] {
      order: 51 !important;
    }

    form[data-testid="form"] > [data-perfumiarz-consent-order="2"] {
      order: 52 !important;
    }

    form[data-testid="form"] > [data-perfumiarz-consent-order="3"] {
      order: 53 !important;
      margin-bottom: 3.6rem !important;
    }

    legend[class*="_checkboxLegend_"] {
      display: none !important;
    }

    label[class*="_checkboxField_"],
    form[data-testid="form"] label:has(input[type="checkbox"]) {
      box-sizing: border-box !important;
      display: grid !important;
      grid-template-columns: 1.2rem minmax(0, 1fr) !important;
      gap: 2.4rem !important;
      align-items: center !important;
      width: 100% !important;
      min-height: 3.8rem !important;
      padding: 1rem 0 !important;
      border-top: 0.05rem solid #c9c9c9 !important;
      cursor: pointer !important;
    }

    fieldset[class*="_checkboxFieldSet_"] > div,
    div[class*="_consentCheckboxContainer_"] > fieldset,
    form[data-testid="form"] > [data-perfumiarz-consent-order] > div {
      box-sizing: border-box !important;
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
    }

    fieldset[class*="_checkboxFieldSet_"] > div[class*="_horizontalLayout_"],
    fieldset[class*="_checkboxFieldSet_"] > div[class*="_verticalLayout_"] {
      display: block !important;
      grid-template-columns: minmax(0, 1fr) !important;
      width: 100% !important;
    }

    [data-perfumiarz-consent-order="3"] label[class*="_checkboxField_"] {
      border-bottom: 0.05rem solid #c9c9c9 !important;
    }

    [data-perfumiarz-consent-last="true"] label:has(input[type="checkbox"]) {
      border-bottom: 0.05rem solid #c9c9c9 !important;
    }

    [data-perfumiarz-consent-order]:has(output[role="alert"])
      label:has(input[type="checkbox"]) {
      background: #edd7d3 !important;
    }

    div[class*="_checkboxWrapper_"] {
      display: block !important;
      width: 1.2rem !important;
      height: 1.2rem !important;
    }

    input[class*="_checkboxInput_"][type="checkbox"] {
      box-sizing: border-box !important;
      display: block !important;
      width: 1.2rem !important;
      height: 1.2rem !important;
      margin: 0 !important;
      appearance: none !important;
      border: 0.05rem solid #9a9a9a !important;
      border-radius: 0.2rem !important;
      background: #f8f9f8 !important;
      box-shadow: inset 0.02rem 0.02rem 0.1rem rgba(0, 0, 0, 0.25) !important;
      cursor: pointer !important;
    }

    input[class*="_checkboxInput_"][type="checkbox"]:checked {
      border-color: #39494b !important;
      background-color: #39494b !important;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12'%3E%3Cpath d='M2.4 6.2 4.8 8.5 9.6 3.4' fill='none' stroke='%23fff' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") !important;
      background-position: center !important;
      background-repeat: no-repeat !important;
      background-size: 100% 100% !important;
    }

    label[class*="_checkboxField_"] span[class*="_textBody_"],
    form[data-testid="form"] label:has(input[type="checkbox"]) span {
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: none !important;
      color: #666 !important;
      font-family: "Overused Grotesk", var(--font-body-family), Arial, sans-serif !important;
      font-size: 1.2rem !important;
      font-weight: 400 !important;
      line-height: 1.3 !important;
      letter-spacing: 0.02em !important;
      word-break: normal !important;
      overflow-wrap: normal !important;
    }

    button[class*="_formSubmitButton_"] {
      box-sizing: border-box !important;
      order: 100 !important;
      display: grid !important;
      grid-template-columns: minmax(0, 1fr) auto 1.4rem !important;
      grid-template-rows: minmax(2rem, auto) !important;
      grid-auto-flow: column !important;
      align-items: center !important;
      column-gap: 3.6rem !important;
      width: 100% !important;
      min-height: 4rem !important;
      padding: 1rem 2.4rem !important;
      border: 0 !important;
      border-radius: 0.2rem !important;
      outline: 0 !important;
      background: #39494b !important;
      color: #f9fbfc !important;
      font-family: "Geist Mono", "Courier New", monospace !important;
      font-size: 1.4rem !important;
      font-weight: 400 !important;
      line-height: 2rem !important;
      letter-spacing: 0.04em !important;
      text-decoration: none !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      cursor: pointer !important;
    }

    button[class*="_formSubmitButton_"] > * {
      grid-column: 2 !important;
      grid-row: 1 !important;
      min-width: 0 !important;
      max-width: 100% !important;
    }

    form[data-testid="form"] > button[type="submit"] {
      order: 100 !important;
      margin: 3.6rem 0 0 !important;
      background: #39494b !important;
      filter: none !important;
    }

    form[data-testid="form"] > button[type="submit"]:hover,
    form[data-testid="form"] > button[type="submit"]:focus-visible,
    form[data-testid="form"] > button[type="submit"]:disabled,
    form[data-testid="form"] > button[type="submit"][aria-busy="true"] {
      background: #39494b !important;
      filter: none !important;
      opacity: 1 !important;
    }

    button[class*="_formSubmitButton_"]::before {
      content: attr(data-perfumiarz-submit-lead);
      grid-column: 1 !important;
      grid-row: 1 !important;
      align-self: center;
      justify-self: stretch;
      height: 1.65rem;
      padding-right: 3.6rem;
      border-right: 0.1rem solid rgba(255, 255, 255, 0.36);
      color: #c9c9c9;
      text-align: left;
      text-decoration: underline;
      text-underline-offset: 0.16em;
    }

    button[class*="_formSubmitButton_"]::after {
      content: "";
      grid-column: 3 !important;
      grid-row: 1 !important;
      display: block;
      width: 1.3969rem;
      height: 1.3969rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 14 14' fill='none'%3E%3Cpath d='M4.65625 11.0586C4.65625 10.6267 4.22962 9.98184 3.79775 9.44055C3.2425 8.74211 2.57898 8.13272 1.81827 7.66768C1.24788 7.31904 0.556422 6.98438 0 6.98438M0 6.98438C0.556422 6.98438 1.24846 6.64971 1.81827 6.30107C2.57898 5.83545 3.2425 5.22606 3.79775 4.52879C4.22962 3.98692 4.65625 3.34086 4.65625 2.91016M0 6.98438L13.9687 6.98438' stroke='%23c9c9c9' stroke-width='0.582031'/%3E%3C/svg%3E");
      background-position: center;
      background-repeat: no-repeat;
      background-size: 100% 100%;
      text-decoration: none !important;
      transform: translateX(0) rotate(180deg);
      transition: transform 0.2s ease;
    }

    button[class*="_formSubmitButton_"]:hover::after,
    button[class*="_formSubmitButton_"]:focus-visible::after {
      transform: translateX(0.4rem) rotate(180deg);
    }

    button[class*="_formSubmitButton_"] > [data-perfumiarz-submit-label] {
      display: block !important;
      color: #f9fbfc !important;
      font: inherit !important;
      text-decoration: underline !important;
      text-underline-offset: 0.16em !important;
      white-space: nowrap !important;
      overflow: hidden !important;
      text-overflow: ellipsis !important;
    }

    div[class*="_formDisclaimer_"] {
      display: none !important;
    }

    a[${PRIVACY_LINK_ATTRIBUTE}] {
      color: inherit !important;
      text-decoration: underline !important;
      text-underline-offset: 0.14em;
    }

    @media screen and (max-width: 749px) {
      section[class*="_formHeader_"] h2 {
        font-size: 1.8rem !important;
        line-height: 1.3 !important;
      }

      label[class*="_checkboxField_"] {
        grid-template-columns: 1.8rem minmax(0, 1fr) !important;
        gap: 2.4rem !important;
      }

      div[class*="_checkboxWrapper_"],
      input[class*="_checkboxInput_"][type="checkbox"] {
        width: 1.8rem !important;
        height: 1.8rem !important;
      }

      button[class*="_formSubmitButton_"] {
        grid-template-columns: auto 1.4rem !important;
        justify-content: end !important;
        column-gap: 2.4rem !important;
        min-height: 5.2rem !important;
        padding: 1rem 2.4rem !important;
      }

      button[class*="_formSubmitButton_"]::before {
        display: none !important;
      }

      button[class*="_formSubmitButton_"] > * {
        grid-column: 1 !important;
        grid-row: 1 !important;
      }

      button[class*="_formSubmitButton_"]::after {
        grid-column: 2 !important;
        grid-row: 1 !important;
      }
    }
  `;

  function addFonts(shadowRoot) {
    if (shadowRoot.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = styles;
    shadowRoot.appendChild(style);
  }

  function normalizeText(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function setElementText(element, value) {
    if (!element || !value || normalizeText(element.textContent) === normalizeText(value)) return;
    element.textContent = value;
  }

  function getFieldLabel(control) {
    if (!control) return null;

    const container = control.closest('div[class*="_formFieldContainer_"]') || control.parentElement;
    return (
      control.closest('label') ||
      container?.querySelector('label[class*="_formInputFieldLabel_"], label') ||
      null
    );
  }

  function setFieldLabel(control, value) {
    if (!control || !value) return;

    if (control.hasAttribute('aria-label')) control.setAttribute('aria-label', value);
    if (control.hasAttribute('placeholder')) control.setAttribute('placeholder', value);

    const label = getFieldLabel(control);
    if (!label) return;

    const textElement = label.querySelector('span[class*="_textBody_"], span');
    if (textElement) {
      setElementText(textElement, value);
      return;
    }

    if (!label.querySelector('input, textarea, select')) {
      setElementText(label, value);
    }
  }

  function localizeFormHeader(shadowRoot, options) {
    const heading = shadowRoot.querySelector('section[class*="_formHeader_"] h2');
    setElementText(heading, options.headingLabel);
  }

  function localizeFields(form, options) {
    const emailInput = form.querySelector('input[type="email"]');
    const nameInput =
      form.querySelector('input[autocomplete="name"], input[name*="name" i]') ||
      Array.from(form.querySelectorAll('input[class*="_formInputField_"]')).find(
        (input) => input.type !== 'email' && input.type !== 'checkbox' && input.type !== 'hidden'
      );
    const messageInput = form.querySelector('textarea');

    setFieldLabel(nameInput, options.nameLabel);
    setFieldLabel(emailInput, options.emailLabel);
    setFieldLabel(messageInput, options.messageLabel);
  }

  function localizeSubject(form, options) {
    // In bilingual mode Shopify already owns the correct PL/EN labels and values.
    // Only hide the other language; never translate or reorder registered choices.
    if (filterBilingualTopics(form, options)) return;
    // Translate visible labels only. Shopify validates submitted choices against
    // its original form configuration; those values must also stay stable for Flow.
    const topicValue = form.querySelector('[class*="_selectToggleText_"]');
    const currentTopic = normalizeText(topicValue?.textContent);

    if (topicValue) {
      topicValue.dataset.perfumiarzTopicPlaceholder = options.topicPlaceholder;

      const selectedIndex = SUBJECT_ALIASES.findIndex((aliases, index) =>
        [...aliases, options.subjectOptions[index]].some(
          (candidate) => normalizeText(candidate) === currentTopic
        )
      );

      if (selectedIndex !== -1) {
        setElementText(topicValue, options.subjectOptions[selectedIndex]);
      }
    }

    const optionElements = Array.from(
      form.querySelectorAll('div[class*="_optionListItem_"] > div')
    );

    optionElements.forEach((optionElement, index) => {
      setElementText(optionElement, options.subjectOptions[index]);
    });
  }

  function getConsentOrder(text) {
    const normalizedText = normalizeText(text);

    if (normalizedText.includes('privacy policy') || normalizedText.includes('polityk')) {
      return '1';
    }

    if (
      normalizedText.includes('marketing') ||
      normalizedText.includes('commercial offers') ||
      normalizedText.includes('marketing communications') ||
      normalizedText.includes('informacji handlowych') ||
      normalizedText.includes('ofert handlowych')
    ) {
      return '2';
    }

    if (
      normalizedText.includes('handle my enquiry') ||
      normalizedText.includes('processing consent') ||
      normalizedText.includes('obsługi mojego zgłoszenia') ||
      normalizedText.includes('realizacji zgłoszenia')
    ) {
      return '3';
    }

    return '';
  }

  function getConsentTextElement(consent) {
    const label = consent.querySelector(
      'label[class*="_checkboxField_"], label:has(input[type="checkbox"])'
    );
    if (!label) return null;

    return (
      label.querySelector('span[class*="_textBody_"]') ||
      Array.from(label.querySelectorAll('span')).find((span) => normalizeText(span.textContent)) ||
      null
    );
  }

  function addPrivacyLink(shadowRoot, privacyPolicyUrl, privacyPolicyLabel) {
    if (!privacyPolicyUrl || privacyPolicyUrl === '#') return;

    const privacyLabels = [privacyPolicyLabel, ...PRIVACY_LABELS].filter(Boolean);

    const walker = document.createTreeWalker(shadowRoot, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) {
      textNodes.push(walker.currentNode);
    }

    textNodes.forEach((textNode) => {
      const parent = textNode.parentElement;
      if (!parent || parent.closest(`[${PRIVACY_LINK_ATTRIBUTE}]`)) return;
      if (['SCRIPT', 'STYLE'].includes(parent.tagName)) return;

      const source = textNode.textContent || '';
      const label = privacyLabels.find((candidate) => source.includes(candidate));
      if (!label) return;

      const start = source.indexOf(label);
      const fragment = document.createDocumentFragment();

      fragment.append(source.slice(0, start));

      const link = document.createElement('a');
      link.href = privacyPolicyUrl;
      link.textContent = label;
      link.setAttribute(PRIVACY_LINK_ATTRIBUTE, 'true');
      link.addEventListener('click', (event) => event.stopPropagation());
      fragment.append(link);

      fragment.append(source.slice(start + label.length));
      textNode.replaceWith(fragment);
    });
  }

  function decorateForm(shadowRoot, options) {
    const form = shadowRoot.querySelector('form[data-testid="form"]');
    if (!form) return false;

    localizeFormHeader(shadowRoot, options);
    localizeFields(form, options);
    localizeSubject(form, options);

    const submitButton = form.querySelector('button[class*="_formSubmitButton_"]');
    if (submitButton) {
      submitButton.dataset.perfumiarzSubmitLead = options.submitLeadLabel;
      if (submitButton.hasAttribute('aria-label')) {
        submitButton.setAttribute('aria-label', options.submitLabel);
      }

      if (!submitButton.querySelector('[data-perfumiarz-submit-label]')) {
        const labelNode = Array.from(submitButton.childNodes).find(
          (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim()
        );

        if (labelNode) {
          const label = document.createElement('span');
          label.dataset.perfumiarzSubmitLabel = 'true';
          label.textContent = options.submitLabel || labelNode.textContent.trim();
          labelNode.replaceWith(label);
        }
      }

      setElementText(
        submitButton.querySelector('[data-perfumiarz-submit-label]'),
        options.submitLabel
      );
    }

    const consentNodes = [];
    const consentLabels = {
      1: options.privacyConsent,
      2: options.marketingConsent,
      3: options.processingConsent,
    };

    Array.from(form.children).forEach((child) => {
      const consentOrder = child.dataset.perfumiarzConsentOrder || getConsentOrder(child.textContent);

      if (!consentOrder) return;

      child.dataset.perfumiarzConsentOrder = consentOrder;
      consentNodes.push(child);
      setElementText(getConsentTextElement(child), consentLabels[consentOrder]);

      [
        child,
        ...child.querySelectorAll(
          'fieldset, [class*="_verticalLayout_"], [class*="_horizontalLayout_"], label, label span'
        ),
      ].forEach(
        (element) => {
          element.style.setProperty('box-sizing', 'border-box', 'important');
          element.style.setProperty('width', '100%', 'important');
          element.style.setProperty('min-width', '0', 'important');
          element.style.setProperty('max-width', 'none', 'important');
          element.style.setProperty('inline-size', '100%', 'important');
          element.style.setProperty('min-inline-size', '0', 'important');
          element.style.setProperty('max-inline-size', 'none', 'important');

          if (
            element.className &&
            (String(element.className).includes('_verticalLayout_') ||
              String(element.className).includes('_horizontalLayout_'))
          ) {
            element.style.setProperty('display', 'block', 'important');
            element.style.setProperty('grid-template-columns', 'minmax(0, 1fr)', 'important');
          }
        }
      );
    });

    const orderedConsents = consentNodes.sort(
      (first, second) =>
        Number(first.dataset.perfumiarzConsentOrder) - Number(second.dataset.perfumiarzConsentOrder)
    );

    orderedConsents.forEach((consent) => {
      delete consent.dataset.perfumiarzConsentLast;
    });

    if (orderedConsents.length) {
      orderedConsents[orderedConsents.length - 1].dataset.perfumiarzConsentLast = 'true';
    }

    if (submitButton && orderedConsents.length) {
      const currentOrder = Array.from(form.children)
        .filter((child) => child.dataset.perfumiarzConsentOrder)
        .map((child) => child.dataset.perfumiarzConsentOrder)
        .join(',');
      const expectedOrder = orderedConsents
        .map((child) => child.dataset.perfumiarzConsentOrder)
        .join(',');
      const lastConsent = orderedConsents[orderedConsents.length - 1];

      if (currentOrder !== expectedOrder || lastConsent.nextElementSibling !== submitButton) {
        orderedConsents.forEach((consent) => {
          submitButton.before(consent);
        });
      }
    }

    options.container.classList.add('is-shopify-form-ready');
    return true;
  }

  function customizeShadowRoot(shadowRoot, options) {
    addFonts(shadowRoot);
    decorateForm(shadowRoot, options);
    addPrivacyLink(shadowRoot, options.privacyPolicyUrl, options.privacyPolicyLabel);

    if (shadowRoot.perfumiarzObserver) return;

    const observer = new MutationObserver(() => {
      addFonts(shadowRoot);
      decorateForm(shadowRoot, options);
      addPrivacyLink(shadowRoot, options.privacyPolicyUrl, options.privacyPolicyLabel);
      scanForShadowRoots(shadowRoot, options);
    });

    observer.observe(shadowRoot, { childList: true, subtree: true });
    shadowRoot.perfumiarzObserver = observer;
  }

  function scanForShadowRoots(root, options) {
    const elements = root.querySelectorAll ? root.querySelectorAll('*') : [];

    elements.forEach((element) => {
      if (element.shadowRoot) {
        customizeShadowRoot(element.shadowRoot, options);
      }
    });
  }

  function initialize(container) {
    const privacyConsent = [
      container.dataset.privacyTextBefore,
      container.dataset.privacyPolicyLabel,
      container.dataset.privacyTextAfter,
    ]
      .filter(Boolean)
      .join(' ');

    const options = {
      language: (container.dataset.formLanguage || document.documentElement.lang || '').toLowerCase().split('-')[0],
      bilingualTopics: container.dataset.bilingualTopics === 'true',
      privacyPolicyUrl: container.dataset.privacyPolicyUrl,
      privacyPolicyLabel: container.dataset.privacyPolicyLabel,
      privacyConsent,
      marketingConsent: container.dataset.marketingConsent,
      processingConsent: container.dataset.processingConsent,
      headingLabel: container.dataset.headingLabel,
      nameLabel: container.dataset.nameLabel,
      emailLabel: container.dataset.emailLabel,
      messageLabel: container.dataset.messageLabel,
      topicPlaceholder: container.dataset.topicPlaceholder || 'Choose message subject',
      subjectOptions: [
        container.getAttribute('data-subject-option-1'),
        container.getAttribute('data-subject-option-2'),
        container.getAttribute('data-subject-option-3'),
        container.getAttribute('data-subject-option-4'),
      ],
      submitLeadLabel: container.dataset.submitLeadLabel || 'Contact form',
      submitLabel: container.dataset.submitLabel || 'Send the message',
      container,
    };

    scanForShadowRoots(container, options);

    const observer = new MutationObserver(() => {
      scanForShadowRoots(container, options);
    });

    observer.observe(container, { childList: true, subtree: true });
  }

  function initializeAll() {
    document.querySelectorAll('[data-shopify-forms-customizer]').forEach((container) => {
      if (container.dataset.shopifyFormsCustomizerReady === 'true') return;
      container.dataset.shopifyFormsCustomizerReady = 'true';
      initialize(container);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
